import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Text } from 'core/ui/atoms';

import useUser from 'core/hooks/useUser';

const MyAccountNavItem = ({ children, subMenus, showMenuHeader = false, onLogoutClick, ...props }) => {
  const [showSubMenu, setShowSubMenu] = useState(false);
  const navItemRef = useRef(null);
  const { currentUser } = useUser();

  const handleToggleSubMenu = (e) => {
    e.stopPropagation();
    setShowSubMenu((prevShowSubMenu) => !prevShowSubMenu);
  };

  const handleClickOutside = (e) => {
    if (navItemRef.current && !navItemRef.current.contains(e.target)) {
      setShowSubMenu(false);
    }
  };

  const handleMenuItemClick = (subMenu) => {
    if (subMenu.isLogout) {
      onLogoutClick();
      setShowSubMenu(false);
    }
  };

  useEffect(() => {
    const handleOutsideClick = (event) => handleClickOutside(event);
    document.addEventListener('click', handleOutsideClick);

    return () => {
      document.removeEventListener('click', handleOutsideClick);
    };
  }, []);

  return (
    <NavItemWrapper ref={navItemRef}>
      <Span onClick={handleToggleSubMenu} {...props}>
        {children}
      </Span>
      {subMenus && showSubMenu && (
        <DropdownMenu>
          {showMenuHeader && <WelcomeText>Welcome, {currentUser?.firstName}{' '}{currentUser?.lastName}</WelcomeText>}
          {subMenus.map((subMenu, index) => {
            if (subMenu.isLogout && subMenu.component) {
              return subMenu.component;
            }
            return (
              <DropdownItem
                key={index}
                href={subMenu.isLogout ? '#' : subMenu.href}
                onClick={subMenu.isLogout ? () => handleMenuItemClick(subMenu) : undefined}
                target={subMenu.target}
              >
                {subMenu.label}
              </DropdownItem>
            );
          })}
        </DropdownMenu>
      )}
    </NavItemWrapper>
  );
};

MyAccountNavItem.propTypes = {
  children: PropTypes.node,
  onLogoutClick: PropTypes.func,
  showMenuHeader: PropTypes.bool,
  subMenus: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      href: PropTypes.string.isRequired,
      isLogout: PropTypes.bool,
      target: PropTypes.string,
    }),
  ),
};

const WelcomeText = styled(Text)`
  font-family: ${({ theme }) => theme.typography.familyLarken};
  color: ${(p) => p.theme.colors.white};
  display: flex;
  flex: 1;
  padding-left: 40px;
  font-size: 14px;
  font-weight: 500;
  font-size: 22px;
  font-style: italic;
  font-weight: 400;
  line-height: 28px;
`;

const NavItemWrapper = styled.div`
  position: relative;
  display: inline-block;
`;

const Span = styled.span`
  text-decoration: none;
  padding: 0.25em 0.5em;
  color: ${(p) => p.theme.typography.colors.primary};
  display: flex;
  align-items: center;
  cursor: pointer;

  ${(p) => p.theme.min('md')`
    font-size: ${(p) => p.theme.typography.sizes.base};
  `};
`;

const DropdownMenu = styled.div`
  display: flex;
  flex-direction: row;
  position: fixed;
  top: 59px;
  height: 64px;
  align-items: center;
  left: 0;
  right: 0;
  width: 100vw;
  background-color: #36443A;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  z-index: 1000;
  justify-content: flex-end;
  gap: 30px;
  padding-right: 20px;
  a {
    color: white;
  }
`;

const DropdownItem = styled.a`
  padding: 12px 16px;
  text-decoration: none;
  color: ${(p) => p.theme.typography.colors.primary || '#333'};
  white-space: nowrap;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 14px;
  font-weight: 400;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);

  &:hover {
    background-color: ${(p) => p.theme.colors.primary || '#e8f4f8'};
    color: ${(p) => p.theme.colors.white || '#fff'};
  }
`;

export default MyAccountNavItem;
